package top.wcpe.allincore.dsl.ir.material

import kotlin.jvm.JvmStatic

/**
 * Material Graph 的跨编译器与运行时共享规格。
 *
 * <p>这里只保存稳定白名单、类型、成本、预算与哈希规则，不依赖任一上层实现。</p>
 *
 * 从 master 的 Java 源迁入 commonMain（纯 Kotlin），供 KMP 两端与 dsl-compiler 共用。
 */
object MaterialGraphContract {
    const val DEFAULT_BUDGET: Int = 16
    const val MAX_BUDGET: Int = 64
    const val MAX_NODES: Int = 32
    const val MAX_EDGES: Int = 64

    private val NODE_PATTERN =
        Regex("([A-Za-z_][A-Za-z0-9_]*)\\s*=\\s*([A-Za-z_][A-Za-z0-9_]*)\\s*\\((.*)\\)")
    private val IDENT_PATTERN = Regex("[A-Za-z_][A-Za-z0-9_]*")

    private val NODE_SPECS: Map<String, NodeSpec> = createNodeSpecs()
    private val FALLBACK_KINDS: List<String> =
        listOf("textureLayer", "materialOverlay", "wornFrame", "runeOverlay")

    @JvmStatic
    fun nodeKinds(): List<String> = NODE_SPECS.keys.toList()

    @JvmStatic
    fun fallbackKinds(): List<String> = FALLBACK_KINDS

    @JvmStatic
    fun isFallbackKind(kind: String?): Boolean = kind != null && FALLBACK_KINDS.contains(kind)

    @JvmStatic
    fun nodeSpec(kind: String?): NodeSpec? = if (kind == null) null else NODE_SPECS[kind]

    @JvmStatic
    fun costOf(kind: String?): Int {
        val spec = nodeSpec(kind)
        return spec?.cost() ?: 0
    }

    /**
     * 动态参数不参与结构哈希，避免交互状态污染静态缓存。
     */
    @JvmStatic
    fun hash(source: String?): String {
        val structures = ArrayList<String>()
        for (statement in statements(source)) {
            val matcher = NODE_PATTERN.matchEntire(statement) ?: continue
            structures.add(
                matcher.groupValues[1] + ":" + matcher.groupValues[2] + ":" +
                    dependencies(matcher.groupValues[3]).joinToString(","),
            )
        }
        return sha256Hex(structures.joinToString(";").encodeToByteArray())
    }

    private fun statements(source: String?): List<String> {
        if (source == null || source.trim().isEmpty()) return emptyList()
        val result = ArrayList<String>()
        // 与 Java StringTokenizer(source, ";\n") 等价：按 ; 与换行拆分
        var i = 0
        val n = source.length
        val buf = StringBuilder()
        while (i < n) {
            val ch = source[i]
            if (ch == ';' || ch == '\n') {
                val trimmed = buf.toString().trim()
                if (trimmed.isNotEmpty() && !trimmed.startsWith("#")) {
                    result.add(trimmed)
                }
                buf.setLength(0)
            } else {
                buf.append(ch)
            }
            i++
        }
        val tail = buf.toString().trim()
        if (tail.isNotEmpty() && !tail.startsWith("#")) {
            result.add(tail)
        }
        return result
    }

    private fun dependencies(args: String): List<String> {
        val dependencies = ArrayList<String>()
        for (part in args.split(',')) {
            val trimmed = part.trim()
            if ('=' !in trimmed && IDENT_PATTERN.matches(trimmed)) {
                dependencies.add(trimmed)
            }
        }
        return dependencies
    }

    /**
     * 纯 Kotlin SHA-256（无 java.security 依赖），输出小写十六进制。
     * 供 commonMain 跨平台使用；结果与 JVM MessageDigest 对齐。
     */
    private fun sha256Hex(message: ByteArray): String {
        val digest = Sha256.digest(message)
        val sb = StringBuilder(digest.size * 2)
        for (b in digest) {
            val v = b.toInt() and 0xff
            sb.append(HEX[(v ushr 4) and 0xf])
            sb.append(HEX[v and 0xf])
        }
        return sb.toString()
    }

    private val HEX = "0123456789abcdef".toCharArray()

    private fun createNodeSpecs(): Map<String, NodeSpec> {
        val specs = LinkedHashMap<String, NodeSpec>()
        specs["texture"] = source(2, ValueType.MATERIAL)
        specs["noise"] = source(2, ValueType.MATERIAL)
        specs["fbm"] = source(4, ValueType.MATERIAL)
        specs["voronoi"] = source(4, ValueType.MATERIAL)
        specs["sdf"] = source(3, ValueType.MASK)
        specs["rune"] = NodeSpec(ValueType.MATERIAL, 2, InputRule.MATERIALS, 0)
        specs["history"] = source(4, ValueType.MATERIAL)
        specs["mix"] = NodeSpec(ValueType.MATERIAL, 1, InputRule.MATERIALS, 2)
        specs["blend"] = NodeSpec(ValueType.MATERIAL, 1, InputRule.MATERIALS, 2)
        specs["edgeWear"] = NodeSpec(ValueType.MATERIAL, 3, InputRule.MATERIALS, 1)
        specs["mask"] = NodeSpec(ValueType.MATERIAL, 1, InputRule.MATERIAL_AND_MASK, 2)
        return specs
    }

    private fun source(cost: Int, output: ValueType): NodeSpec =
        NodeSpec(output, cost, InputRule.NONE, 0)

    enum class ValueType {
        MATERIAL,
        MASK,
    }

    enum class InputRule {
        NONE,
        MATERIALS,
        MATERIAL_AND_MASK,
    }

    class NodeSpec(
        private val output: ValueType,
        private val cost: Int,
        private val inputRule: InputRule,
        private val minimumInputs: Int,
    ) {
        fun output(): ValueType = output

        fun cost(): Int = cost

        fun accepts(inputs: List<ValueType>?): Boolean {
            val list = inputs ?: emptyList()
            return when (inputRule) {
                InputRule.NONE -> list.isEmpty()
                InputRule.MATERIAL_AND_MASK ->
                    list.size == 2 &&
                        list[0] == ValueType.MATERIAL &&
                        list[1] == ValueType.MASK
                InputRule.MATERIALS ->
                    list.size >= minimumInputs && list.all { it == ValueType.MATERIAL }
            }
        }
    }

    /**
     * 最小纯 Kotlin SHA-256 实现（FIPS 180-4），避免 commonMain 依赖 java.security。
     */
    private object Sha256 {
        private val K = intArrayOf(
            0x428a2f98, 0x71374491, -0x4a3f0431, -0x164a245b,
            0x3956c25b, 0x59f111f1, -0x6dc07d5c, -0x54e3a12b,
            -0x27f85568, 0x12835b01, 0x243185be, 0x550c7dc3,
            0x72be5d74, -0x7f214e02, -0x6423f959, -0x3e640e8c,
            -0x1b64963f, -0x1041b87a, 0x0fc19dc6, 0x240ca1cc,
            0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
            -0x67c1aeae, -0x57ce3993, -0x4ffcd838, -0x40a68039,
            -0x391ff40d, -0x2a586eb9, 0x06ca6351, 0x14292967,
            0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
            0x650a7354, 0x766a0abb, -0x7e3d36d2, -0x6d8dd37b,
            -0x5d40175f, -0x57e599b5, -0x3db47490, -0x3893ae5d,
            -0x2e6d17e7, -0x2966f9dc, -0xbf1ca7b, 0x106aa070,
            0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
            0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
            0x748f82ee, 0x78a5636f, -0x7b3787ec, -0x7338fdf8,
            -0x6f410006, -0x5baf9315, -0x41065c09, -0x398e870e,
        )

        fun digest(message: ByteArray): ByteArray {
            val h = intArrayOf(
                0x6a09e667, -0x4498517b, 0x3c6ef372, -0x5ab00ac6,
                0x510e527f, -0x64fa9774, 0x1f83d9ab, 0x5be0cd19,
            )
            val bitLen = message.size.toLong() * 8L
            val withOne = message.size + 1
            var paddedLen = withOne + 8
            val rem = paddedLen % 64
            if (rem != 0) paddedLen += 64 - rem
            val padded = ByteArray(paddedLen)
            message.copyInto(padded)
            padded[message.size] = 0x80.toByte()
            // 大端 64-bit 长度写在末 8 字节
            for (i in 0 until 8) {
                padded[paddedLen - 1 - i] = ((bitLen ushr (8 * i)) and 0xff).toByte()
            }
            val w = IntArray(64)
            var offset = 0
            while (offset < paddedLen) {
                for (i in 0 until 16) {
                    val j = offset + i * 4
                    w[i] = ((padded[j].toInt() and 0xff) shl 24) or
                        ((padded[j + 1].toInt() and 0xff) shl 16) or
                        ((padded[j + 2].toInt() and 0xff) shl 8) or
                        (padded[j + 3].toInt() and 0xff)
                }
                for (i in 16 until 64) {
                    val s0 = rotr(w[i - 15], 7) xor rotr(w[i - 15], 18) xor (w[i - 15] ushr 3)
                    val s1 = rotr(w[i - 2], 17) xor rotr(w[i - 2], 19) xor (w[i - 2] ushr 10)
                    w[i] = w[i - 16] + s0 + w[i - 7] + s1
                }
                var a = h[0]
                var b = h[1]
                var c = h[2]
                var d = h[3]
                var e = h[4]
                var f = h[5]
                var g = h[6]
                var hh = h[7]
                for (i in 0 until 64) {
                    val s1 = rotr(e, 6) xor rotr(e, 11) xor rotr(e, 25)
                    val ch = (e and f) xor (e.inv() and g)
                    val t1 = hh + s1 + ch + K[i] + w[i]
                    val s0 = rotr(a, 2) xor rotr(a, 13) xor rotr(a, 22)
                    val maj = (a and b) xor (a and c) xor (b and c)
                    val t2 = s0 + maj
                    hh = g
                    g = f
                    f = e
                    e = d + t1
                    d = c
                    c = b
                    b = a
                    a = t1 + t2
                }
                h[0] += a
                h[1] += b
                h[2] += c
                h[3] += d
                h[4] += e
                h[5] += f
                h[6] += g
                h[7] += hh
                offset += 64
            }
            val out = ByteArray(32)
            for (i in 0 until 8) {
                out[i * 4] = (h[i] ushr 24).toByte()
                out[i * 4 + 1] = (h[i] ushr 16).toByte()
                out[i * 4 + 2] = (h[i] ushr 8).toByte()
                out[i * 4 + 3] = h[i].toByte()
            }
            return out
        }

        private fun rotr(x: Int, n: Int): Int = (x ushr n) or (x shl (32 - n))
    }
}
